import { useChat } from "@/contexts/ChatContext";

import { EMPTY_STATE_BODY, EMPTY_STATE_HEADING } from "./chatCopy";
import { suggestionsForRouteType } from "./suggestions";

/**
 * The greeting and the route-aware chips (D-02, D-15).
 *
 * ZERO LLM COST UNTIL THE FIRST REAL MESSAGE. The chips are fixed template sets selected by route
 * type, not generated: generating three of them per page load would put a model call in front of
 * every visitor who never opens the panel.
 *
 * THE PATHNAME NEVER REACHES THE RENDER (T-38-81). `routeType` comes off the context, which resolves
 * it through `suggestions.resolveChipRouteType`; the route type then selects a constant array. A
 * crafted URL can choose WHICH of three fixed sets appears and nothing else.
 *
 * RENDERED ONLY WHILE THE THREAD IS EMPTY. `ChatPanel` owns that condition; once any turn exists the
 * chips are gone and do not return.
 *
 * The chips are 44px tall and that is deliberate: hit area, not boldness, is what makes them
 * tappable, which is why the panel's type scale has no 500 weight anywhere.
 */
export default function ChatEmptyState() {
  const { routeType, sendMessage, composerLocked } = useChat();
  const chips = suggestionsForRouteType(routeType);

  return (
    <div className="flex flex-col items-center px-4 py-6 text-center">
      <img
        src="/brand/mascot.png"
        alt=""
        aria-hidden="true"
        className="h-16 w-16 object-contain"
        loading="lazy"
      />

      <h2
        className="mt-6"
        style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3, color: "var(--ys-ink)" }}
      >
        {EMPTY_STATE_HEADING}
      </h2>

      <p
        className="mt-2"
        style={{ fontSize: 14, fontWeight: 400, lineHeight: 1.6, color: "var(--ys-ink-2)" }}
      >
        {EMPTY_STATE_BODY}
      </p>

      {/* A chip tap is a prefilled send. `sendMessage` owns the `chat_suggestion_clicked` capture and
          carries the stable `chip_id` — never the chip's Arabic text, which would put prose in an
          event property and break on the next copy edit. */}
      <div className="mt-6 flex w-full flex-col gap-2">
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            disabled={composerLocked}
            onClick={() => sendMessage({ text: chip.text, chipId: chip.id })}
            className="flex min-h-11 w-full items-center justify-center rounded-[var(--ys-r-pill)] border border-[var(--ys-rule)] bg-[var(--ys-bg-2)] px-4 py-2 transition-colors hover:bg-[var(--ys-primary-soft)] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ys-ink-2)] focus-visible:ring-offset-2"
            style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.5, color: "var(--ys-ink-2)" }}
          >
            {chip.text}
          </button>
        ))}
      </div>
    </div>
  );
}
